import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supportsWebPush, getWebPushConfig, syncWebPush, enableWebPush, disableWebPush } from '@/lib/webPush';

export default function WebPushSettings() {
  const { user, impersonating } = useAuth();
  const { pathname } = useLocation();
  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const settings = pathname === '/notification-preferences';
  const supported = supportsWebPush();

  useEffect(() => {
    if (!supported || !user || impersonating) return;
    let cancelled = false;
    setConfig(null);
    setEnabled(false);
    setError('');
    setDismissed(sessionStorage.getItem(`web-push-dismissed:${user._id || user.id}`) === '1');
    getWebPushConfig().then(async value => {
      if (!cancelled) setConfig(value);
      const subscribed = value.enabled && await syncWebPush();
      if (!cancelled) { setConfig(value); setEnabled(subscribed); }
    }).catch(() => { if (!cancelled) setError('Could not sync notification settings. Try Enable notifications again, or reopen this page.'); });
    return () => { cancelled = true; };
  }, [supported, user?._id, user?.id, impersonating]);

  if (!supported || !user || impersonating) return null;
  if (!settings && (enabled || dismissed || !config?.enabled || !['/', '/dashboard', '/tasks'].includes(pathname))) return null;

  const toggle = async () => {
    setBusy(true);
    setError('');
    try {
      if (enabled) { await disableWebPush(); setEnabled(false); }
      else { await enableWebPush(config.publicKey); setEnabled(true); }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return <div className="border-b border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
    <div className="flex flex-wrap items-center gap-3">
      <p className="flex-1">{enabled ? 'Task push notifications are enabled on this device.' : 'Get a notification when a task is assigned to you.'}</p>
      <button type="button" disabled={busy || !config?.enabled} onClick={toggle} className="rounded-md bg-indigo-600 px-3 py-2 font-semibold text-white disabled:opacity-50">
        {busy ? 'Saving…' : enabled ? 'Disable on this device' : 'Enable notifications'}
      </button>
      {!settings && <button type="button" onClick={() => {
        sessionStorage.setItem(`web-push-dismissed:${user._id || user.id}`, '1');
        setDismissed(true);
      }}>Not now</button>}
    </div>
    {settings && config?.enabled === false && <p className="mt-2">Push notifications are not available yet.</p>}
    {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
  </div>;
}
